import { Component, EventEmitter, Input, Output } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IRanchWellViewModel } from '../../ranch-settings/interfaces';
import { RanchCreateWellDialog } from '../dialogs/well-dialog';

@Component({
	moduleId: module.id,
	selector: 'ranch-create-wells-step',
	templateUrl: 'step-wells.html',
	styleUrls: ['../ranch-create.scss']
})

export class RanchCreateWellsStepComponent {
	@Input()
	public form: FormArray;

	@Input()
	public subscriptions$: Subject<boolean>;

	@Output()
	public nextStep: EventEmitter<number> = new EventEmitter<number>();

	@Output()
	public onBack: EventEmitter<void> = new EventEmitter<void>();

	@Output()
	public onCancel: EventEmitter<void> = new EventEmitter<void>();

	public get wellControls(): AbstractControl[] {
		if (!this.form) {
			return [];
		}

		return this.form.controls;
	}

	constructor(
		private _dialog: MatDialog,
		private _fb: FormBuilder
	) { }

	public back(): void {
		this.onBack.emit();
	}

	public cancel(): void {
		this.onCancel.emit();
	}

	public deleteWell(index: number, event?: MouseEvent): void {
		if (event) {
			event.stopPropagation();
		}

		this.form.removeAt(index);
		this.form.markAsDirty();
	}

	public next(): void {
		this.form.markAllAsTouched();
		this.form.updateValueAndValidity();

		if (!this.form.valid) {
			return;
		}

		this.nextStep.emit(5);
	}

	public openAddWellDialog(): void {
		this._openWellDialog((well: IRanchWellViewModel) => {
			if (!well) {
				return;
			}

			this.form.push(this._initializeForm(well));
			this.form.markAsDirty();
		});
	}

	public openEditWellDialog(index: number, event?: MouseEvent): void {
		let well: IRanchWellViewModel;

		if (event) {
			event.stopPropagation();
		}

		well = this.form.at(index).value;

		this._openWellDialog((updatedWell: IRanchWellViewModel) => {
			if (!updatedWell) {
				return;
			}

			this.form.setControl(index, this._initializeForm(updatedWell));
			this.form.markAsDirty();
		}, well);
	}

	private _initializeForm(well: IRanchWellViewModel): FormGroup {
		return this._fb.group({
			Id: [well.Id],
			Name: [well.Name, [Validators.required, Validators.maxLength(128)]],
			ElectricalConductivity: [well.ElectricalConductivity, [
				Validators.min(0.00001),
				Validators.max(2147483627)
			]],
			NitrogenPPM: [well.NitrogenPPM, [
				Validators.required,
				Validators.min(0.0000001),
				Validators.max(2147483627)
			]]
		});
	}

	private _openWellDialog(onClose: (well: IRanchWellViewModel) => void, well?: IRanchWellViewModel): void {
		this._dialog.open(RanchCreateWellDialog, {
			width: '500px',
			data: {
				well: well
			}
		}).afterClosed().pipe(takeUntil(this.subscriptions$)).subscribe(onClose);
	}
}
